/**
 * Em'z Blendz — Mobile Nav Drawer Focus Trap
 * Version: 1.0
 *
 * Watches for the drawer--is-open class on #NavDrawer and:
 *   1. Traps Tab / Shift+Tab inside the drawer
 *   2. Sets `inert` on main content + footer so screen readers skip them
 *   3. Focuses the close button when drawer opens
 *   4. Returns focus to the hamburger button when drawer closes
 *
 * Uses MutationObserver on class changes — zero overhead when drawer is closed.
 * Gracefully degrades: if any element is missing, it just skips that step.
 */
(function() {
  'use strict';

  var drawer = document.getElementById('NavDrawer');
  if (!drawer) return;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var menuButton = document.querySelector('.js-drawer-open-nav');
  var isTrapping = false;
  var openFocusTimer = null;
  var returnFocusTimer = null;
  var focusCycle = 0;

  // A delayed focus move must never interrupt a tap, keystroke or chosen focus.
  function cancelOpeningFocus() {
    window.clearTimeout(openFocusTimer);
    openFocusTimer = null;
    drawer.removeEventListener('pointerdown', cancelOpeningFocus, true);
    drawer.removeEventListener('touchstart', cancelOpeningFocus, true);
    drawer.removeEventListener('keydown', cancelOpeningFocus, true);
    document.removeEventListener('focusin', cancelOpeningFocus, true);
  }

  function cancelReturnFocus() {
    window.clearTimeout(returnFocusTimer);
    returnFocusTimer = null;
    document.removeEventListener('focusin', cancelReturnFocus, true);
  }

  /**
   * Get all visible focusable elements inside the drawer.
   */
  function getFocusableElements() {
    return Array.prototype.filter.call(
      drawer.querySelectorAll(FOCUSABLE),
      function(el) { return el.offsetParent !== null && el.getClientRects().length > 0 &&
          getComputedStyle(el).visibility !== 'hidden'; }
    );
  }

  /**
   * Keydown handler: wrap focus at drawer boundaries, close on Escape.
   */
  function onKeyDown(e) {
    if (e.key === 'Escape') {
      var closeBtn = drawer.querySelector('.js-drawer-close');
      if (closeBtn) closeBtn.click();
      return;
    }

    if (e.key !== 'Tab') return;

    var focusable = getFocusableElements();
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /**
   * Mark content outside the drawer as inert (unfocusable + hidden from AT).
   * We target #MainContent and footer sections — NOT the header section
   * (which contains the drawer itself).
   */
  function setInert(add) {
    var targets = [
      document.getElementById('MainContent'),
      document.querySelector('.shopify-section-group-footer-group'),
      document.querySelector('[id*="footer"].shopify-section')
    ];
    targets.forEach(function(el) {
      if (!el) return;
      if (add) {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
      } else {
        el.removeAttribute('inert');
        el.removeAttribute('aria-hidden');
      }
    });
  }

  function onDrawerOpen() {
    if (isTrapping) return;
    isTrapping = true;
    cancelOpeningFocus();
    cancelReturnFocus();
    var cycle = ++focusCycle;

    setInert(true);
    document.addEventListener('keydown', onKeyDown);

    // Retain keyboard entry after the animation starts, unless the shopper acts first.
    var closeBtn = drawer.querySelector('.js-drawer-close');
    var initialFocus = document.activeElement;
    if (closeBtn && (initialFocus === drawer || initialFocus === menuButton ||
        initialFocus === document.body || initialFocus === document.documentElement)) {
      drawer.addEventListener('pointerdown', cancelOpeningFocus, true);
      drawer.addEventListener('touchstart', cancelOpeningFocus, { capture: true, passive: true });
      drawer.addEventListener('keydown', cancelOpeningFocus, true);
      document.addEventListener('focusin', cancelOpeningFocus, true);
      openFocusTimer = window.setTimeout(function() {
        cancelOpeningFocus();
        if (cycle !== focusCycle || !isTrapping || !drawer.isConnected ||
            !drawer.classList.contains('drawer--is-open') ||
            !closeBtn.isConnected || !drawer.contains(closeBtn) || closeBtn.disabled ||
            document.activeElement !== initialFocus) return;
        closeBtn.focus({ preventScroll: true });
      }, 150);
    }
  }

  function onDrawerClose() {
    if (!isTrapping) return;
    isTrapping = false;
    cancelOpeningFocus();
    cancelReturnFocus();
    var cycle = ++focusCycle;

    setInert(false);
    document.removeEventListener('keydown', onKeyDown);

    // Return focus only while this close still owns it; a rapid reopen or new choice wins.
    var initialFocus = document.activeElement;
    if (menuButton && (initialFocus === document.body || initialFocus === document.documentElement ||
        drawer.contains(initialFocus))) {
      document.addEventListener('focusin', cancelReturnFocus, true);
      returnFocusTimer = window.setTimeout(function() {
        cancelReturnFocus();
        if (cycle !== focusCycle || isTrapping || !drawer.isConnected ||
            drawer.classList.contains('drawer--is-open') || !menuButton.isConnected ||
            !menuButton.getClientRects().length || getComputedStyle(menuButton).visibility === 'hidden' ||
            menuButton.closest('[inert]') || document.activeElement !== initialFocus) return;
        menuButton.focus({ preventScroll: true });
      }, 50);
    }
  }

  // Watch for class changes on the drawer element
  var observer = new MutationObserver(function(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].attributeName === 'class') {
        if (drawer.classList.contains('drawer--is-open')) {
          onDrawerOpen();
        } else {
          onDrawerClose();
        }
        break;
      }
    }
  });

  observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
})();
